import React from "react";
import { useStyles } from "./DeliveryCard.styles";
import {
  OrderLine,
  OrderState,
} from "../../../pages/OrderDetailsPage/OrderDetailsTypes";
import OrderTimeLine from "../../common/order-time-line/order-time-line.component";
import { ReactComponent as HeartIcon } from "../../../assets/heart.svg";
import { ReactComponent as BasketIcon } from "../../../assets/basketadd.svg";
import { useTranslation } from "react-i18next";
import Tooltip from "@mui/material/Tooltip";

import Currency from "../../../shared/components/currency/Currency";
import { getDateInfo } from "../../../shared/dates";
import { Snackbar } from "@material-ui/core";
import { useDeliveryCard } from "../hooks/useDeliveryCard";
import CircleLoader from "../../common/loader/circle-loader.component";

interface DeliveryCardProps {
  index: number; // delivery index (1)
  total: number; // total deliveries (3)
  state: OrderState;
  deliveryDate: string | null;
  timeWindow?: string | null;
  items: OrderLine[];
}

const DeliveryCard: React.FC<DeliveryCardProps> = ({
  index,
  total,
  state,
  deliveryDate,
  timeWindow,
  items,
}) => {
  const { t } = useTranslation();
  const classes = useStyles();

  const { addItemToOrder } = useDeliveryCard();
  const [snackOpen, setSnackOpen] = React.useState(false);
  const [snackMessage, setSnackMessage] = React.useState("");
  const [loadingVariantId, setLoadingVariantId] = React.useState<string | null>(
    null
  );

  const dateInfo = getDateInfo(timeWindow);
  const customFormattedDate = dateInfo?.customFormattedDate;

  const handleAddToCart = async (variantId: string, qty: number) => {
    setLoadingVariantId(variantId);

    const result = await addItemToOrder(variantId, qty);

    if (!result.ok) {
      // Only if mutation failed — show snackbar
      const key = `errors.addItemToOrder.${result.type}`;
      const translated = t(key);

      setSnackMessage(translated !== key ? translated : t("errors.generic"));

      setSnackOpen(true);
    }

    setLoadingVariantId(null);
  };

  // TODO - timeWindow should be in USE once we have a proper response

  return (
    <>
      <div className={classes.card}>
        {/* HEADER */}
        {state !== OrderState.CANCELLED ? (
          <div className={classes.headerRow}>
            {state === OrderState.COMPLETED ? (
              <div className={classes.deliveryIndex}>
                <span className={classes.deliveryIndexHeader}>
                  {t("account.delivered")}
                </span>
                <span className={classes.deliveryIndexDate}>
                  {customFormattedDate}
                </span>
              </div>
            ) : (
              <>
                {total > 1 ? (
                  <div className={classes.deliveryIndex}>
                    <span className={classes.deliveryIndexHeader}>
                      {t("account.deliveryIndex", { index, total })}
                    </span>
                    <span className={classes.deliveryIndexDate}>
                      {customFormattedDate}
                    </span>
                  </div>
                ) : (
                  <div className={classes.deliveryIndex}>
                    <span className={classes.deliveryIndexHeader}>
                      {t("account.delivery")}
                    </span>
                    <span className={classes.deliveryIndexDate}>
                      {customFormattedDate}
                    </span>
                  </div>
                )}
              </>
            )}

            {/* INFO SECTION */}
            <OrderTimeLine state={state} />
          </div>
        ) : null}

        {/* ITEMS LIST */}
        <div className={classes.itemsList}>
          <span className={classes.itemsListTotal}>
            {items?.length}{" "}
            {items?.length === 1 ? t("common.item") : t("common.items")}
          </span>
          {items?.map((item) => (
            <>
              <div key={item.id} className={classes.itemRow}>
                {/* LEFT — image + name */}
                <div className={classes.left}>
                  <img
                    src={item?.productVariant?.featuredAsset?.preview}
                    alt={item?.productVariant?.name}
                    className={classes.image}
                  />
                  <span className={classes.name}>
                    {item?.productVariant?.name}
                  </span>
                </div>

                {/* CENTER — icons + qty */}
                <div className={classes.center}>
                  <HeartIcon className={classes.heartIcon} />
                  <Tooltip
                    title={`${t("account.copyLineToBasket")}`}
                    placement="top"
                  >
                    <div
                      className={classes.basketIcon}
                      onClick={() => {
                        if (
                          !item?.quantity ||
                          !item?.productVariant?.id ||
                          loadingVariantId
                        )
                          return;

                        handleAddToCart(item.productVariant.id, item?.quantity);
                      }}
                    >
                      {loadingVariantId === item.productVariant.id ? (
                        <CircleLoader size={12} color="#FFF" />
                      ) : (
                        <BasketIcon />
                      )}
                    </div>
                  </Tooltip>
                  <div className={classes.qty}>
                    <span className={classes.qtyLabel}>{t("account.Qty")}</span>
                    <span className={classes.qtyValue}>{item?.quantity}</span>
                  </div>
                </div>

                {/* RIGHT — price */}
                <div className={classes.right}>
                  <Currency />
                  {(item?.linePriceWithTax / 100 || 0)?.toFixed(2)}
                </div>
              </div>
            </>
          ))}
        </div>
      </div>
      {/* Snackbar UI */}
      <Snackbar
        open={snackOpen}
        autoHideDuration={3000}
        onClose={() => setSnackOpen(false)}
        message={snackMessage}
      />
    </>
  );
};

export default DeliveryCard;
